import {isString, replace, startCase} from 'lodash';
import urlLib from 'url';
import SharedConfig from '@packages/systems/core/constants/SharedConfig';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {SiteType} from '@packages/systems/core/records/Site';

/**
 * Parses and returns the email address from a mailto: url
 * @param  {String} href the mailto url ie: the value of the anchor's href
 * @return {String}      the email address
 */
export const parseEmailAddress = function (href: string) {
  const match = href.match(SharedConfig.MAILTO_EMAIL_REGEX);
  return match ? match[1] : '';
};

// pathToPage is something like "/folder1/folder2/collection-slug"
// We want to remove the page collection and keep the folders leading up to it
// since the existing href parts will contain the collection slug,
// like "/collection-slug/item-slug"
export const removeCollectionSlugFromUrlPath = function (
  pathToPage: string
): string {
  const parts = pathToPage.split('/');
  const lastIndex = parts.length - 1;
  return parts.length > 1 ? parts.slice(0, lastIndex).join('/') : pathToPage;
};

/**
 * Prases and returns the email subject from a mailto: url
 * @param  {String} href             the href value ie: mailto url
 * @return {String}                  the email subject
 */
export const parseEmailSubject = function (href: string) {
  const subjectMatch = href.match(SharedConfig.MAILTO_SUBJECT_REGEX);
  if (subjectMatch && subjectMatch.length === 2) {
    try {
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      return decodeURIComponent(subjectMatch[1]);
    } catch (e: any) {
      console.warn('Parsed a malformed email subject from href [%s]', href);
      return subjectMatch[1];
    }
  } else {
    return '';
  }
};

/**
 * Parses and returns the telephone number from a tel: url
 * @param  {String} href the tel url ie: the value of the anchor's href
 * @return {String}      the telephone number
 */
export const parseTel = function (href: string) {
  const match = href.match(SharedConfig.TEL_NUMBER_REGEX);
  return match ? match[1] : '';
};

/**
 * Detects whethers a string starts with an uploaded file ID.
 * @param  {String}  str The string to check.
 * @return {Boolean}     True if the string starts with a mongo GUID or a legacy random GUID string.
 */
export const hasGuid = function (str: string) {
  if (!str || !isString(str)) {
    return false;
  }

  return (
    /^[0-9a-fA-F]{24}_/.test(str) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}_/.test(str)
  );
};

/**
 * Determins if an image URL looks like a Webflow upload URL.
 * @param  {String}  imageUrl The image URL
 * @return {Boolean}          True if it is a webflow image
 */
export const isWebflowImage = function (imageUrl: string) {
  if (!imageUrl || !isString(imageUrl)) {
    return false;
  }

  imageUrl = imageUrl.toLowerCase();

  // If it looks like there is a GUID as a part of the file name, it's a Webflow upload
  if (hasGuid(imageUrl)) {
    return true;
  }

  // If it looks like there is a Mongo SiteID in the URL, it's likely a WF upload
  if (/\/[0-9a-fA-F]{24}\//.test(imageUrl)) {
    return true;
  }

  return false;
};

/**
 * Determines whether a string starts with a URL encoded site ID and a GUID combination e.g. {siteId}%2F{guid}.
 * @param  {String}  urlString The URL string
 * @return {Boolean}          True if the string starts with a URL encoded site ID and a GUID combination e.g. {siteId}%2F{guid}.
 */
export const hasSiteIdAndGuid = function (urlString: string) {
  if (!urlString || !isString(urlString)) {
    return false;
  }

  // This regex looks for a composite id where a site ID and a GUID are separated by a urlencoded foraward slash '%2F' (or '%2f' for lowercase strings) in the path
  if (/^[0-9a-fA-F]{24}%2(F|f)[0-9a-fA-F]{24}_/.test(urlString)) {
    return true;
  }

  return false;
};

/**
 * Returns an asset URL with a correct CDN.
 * @param  {String}  url        Original URL
 * @param  {Boolean} sslOk      Flag telling if SSL should be used
 * @param  {String}  cdnHost    Webflow CDN host
 * @return {String}             Url with a correct CDN url.
 */
export const getCdnUrl = function (
  url: string,
  sslOk: boolean,
  cdnHost?: string | null
) {
  const urlObject = urlLib.parse(url);
  const protocol = sslOk ? 'https:' : 'http:';

  // Some URLs, like the placeholder asset URLs defined in
  // packages/systems/core/constants/SharedConfig, are hard-coded to
  // point to the production asset CDN `d3e54v103j8qbb.cloudfront.net`.
  // In non-prod AWS accounts this function tries to rewrite those
  // URLs to point to non-existent objects. To prevent this, in CI and
  // local dev, we preserve the hard-coded production CDN host.
  let host = cdnHost || urlObject.host;
  if (
    process.env.NODE_ENV !== 'production' &&
    ['development', 'test'].includes(process.env.NODE_ENV || 'development') &&
    urlObject.host === 'cdn.dev.website-files.com'
  ) {
    host = urlObject.host;
  }

  return urlLib.format({
    protocol,
    host,
    pathname: urlObject.pathname,
  });
};

/**
 * Returns a function that will handle updating a src url
 * with the correct cdn url based on runtimeEnvironment and site options.
 * @param site
 * @param runtimeEnvironment
 * @returns (src: string) => string
 */
export const handleCdnUrl =
  (site: SiteType, runtimeEnvironment: Environment) => (src: string) => {
    const {sslOk, cdnHost} = site;

    if (runtimeEnvironment === WebflowEnvironments.SSR) {
      return getCdnUrl(src, sslOk, cdnHost);
    } else {
      return src;
    }
  };

export const generateUrl = function (
  protocol: string,
  host: string,
  pathname: string,
  search: string
) {
  return search.length
    ? `${protocol}//${host}${pathname}?${search}`
    : `${protocol}//${host}${pathname}`;
};

type generateReadOnlyLinkParams = {
  protocol: string;
  host: string;
  shortName: string;
  previewId: string;
  pageId?: string | null;
  itemId?: string | null;
  locale?: string | null;
};

// This function generates read-only link shared from Designer
// The order of parameters is important
export const generateReadOnlyLink = function (
  opts: generateReadOnlyLinkParams
) {
  const {protocol, host, shortName, previewId, pageId, itemId, locale} = opts;
  const query = {
    utm_medium: 'preview_link',
    utm_source: 'designer',
    utm_content: shortName,
    preview: previewId,
  } as const;

  if (pageId) {
    // @ts-expect-error - TS2339 - Property 'pageId' does not exist on type '{ readonly utm_medium: "preview_link"; readonly utm_source: "designer"; readonly utm_content: string; readonly preview: string; }'.
    query.pageId = pageId;
  }

  if (itemId) {
    // @ts-expect-error - TS2339 - Property 'itemId' does not exist on type '{ readonly utm_medium: "preview_link"; readonly utm_source: "designer"; readonly utm_content: string; readonly preview: string; }'.
    query.itemId = itemId;
  }

  if (locale) {
    // @ts-expect-error - TS2339 - Property 'locale' does not exist on type '{ readonly utm_medium: "preview_link"; readonly utm_source: "designer"; readonly utm_content: string; readonly preview: string; }'.
    query.locale = locale;
  }

  // @ts-expect-error - TS2339 - Property 'workflow' does not exist on type '{ readonly utm_medium: "preview_link"; readonly utm_source: "designer"; readonly utm_content: string; readonly preview: string; }'.
  query.workflow = 'preview';

  const queryStr = new URLSearchParams(query).toString();

  const isCurrentlyInPreview = host.startsWith('preview.');

  // When on a preview domain, drop the `preview.` label so that we remain with the correct host, i.e `webflow.com`.
  // Otherwise, we're using designer subdomain (our URL is slug.design.bar.baz), so remove
  // the first two labels (slug.design)
  const cookedHost = isCurrentlyInPreview
    ? host.replace(/^preview\./, '')
    : host.split('.').slice(2).join('.');

  // ensure protocol ends with a `:`
  const cookedProtocol = protocol.endsWith(':') ? protocol : `${protocol}:`;

  return `${cookedProtocol}//preview.${cookedHost}/preview/${shortName}?${queryStr}`;
};

type MatchmakingFormURLQueryParams = {
  ref: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string | null | undefined;
};

export const MATCHMAKING_FORM_URL = 'https://experts.webflow.com/hire/new';

/**
 * Builds the matchmaking form url with query parameters.
 *
 * The matchmaking form will extract these fields to auto-populate fields.
 * We have to send the values in the form's desired format.
 * @param  {MatchmakingFormURLQueryParams}  queryParams Query Parameters to build the URL
 * @return {String}             Matchmaking URL with formatted query parameters
 */
export const buildMatchmakingFormURL = (
  queryParams: MatchmakingFormURLQueryParams
): string => {
  const formattedQueryParams: Array<any | string> = [];

  Object.keys(queryParams).forEach((key) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'MatchmakingFormURLQueryParams'.
    let value = queryParams[key] || '';

    // Handle the role value format
    // Example: 'web-designer -> Web%20Designer
    if (key === 'role') {
      value = replace(value, '/-/g', ' ');
      value = startCase(value);
    }

    formattedQueryParams.push(key + '=' + encodeURIComponent(value));
  });

  return MATCHMAKING_FORM_URL + '?' + formattedQueryParams.join('&');
};

export const CODED_MATCHMAKING_FORM_URL = '/dashboard/certified-partners';

/**
 * Builds the matchmaking form url with query parameters.
 *
 * The matchmaking form will extract these fields to auto-populate fields.
 * We have to send the values in the form's desired format.
 * @param  {MatchmakingFormURLQueryParams}  queryParams Query Parameters to build the URL
 * @return {String}             Matchmaking URL with formatted query parameters
 */
export const buildCodedMatchmakingFormURL = (
  queryParams: MatchmakingFormURLQueryParams
): string => {
  const formattedQueryParams: Array<any | string> = [];

  Object.keys(queryParams).forEach((key) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'MatchmakingFormURLQueryParams'.
    let value = queryParams[key] || '';

    // Handle the role value format
    // Example: 'web-designer -> Web%20Designer
    if (key === 'role') {
      value = replace(value, '/-/g', ' ');
      value = startCase(value);
    }

    formattedQueryParams.push(key + '=' + encodeURIComponent(value));
  });

  if (process.env.NODE_ENV !== 'production') {
    formattedQueryParams.push(`test=${encodeURIComponent('beta')}`);
  }

  return CODED_MATCHMAKING_FORM_URL + '?' + formattedQueryParams.join('&');
};

type generateUnauthenticatedCommentsProjectLinkParams = {
  protocol: string;
  host: string;
  shortName: string;
  projectLinkId: string;
  workflow?: string;
  commentId?: string;
  pageId?: string;
  itemId?: string;
  locale?: string;
};

export const generateUnauthenticatedCommentsProjectLink = function (
  opts: generateUnauthenticatedCommentsProjectLinkParams
) {
  const {shortName} = opts;

  const {cookedProtocol, cookedHost, queryStr} = getSharedLinkParams(opts);

  return `${cookedProtocol}//comments.${cookedHost}/comment/${shortName}?${queryStr}`;
};

type generateWorkspaceMemberProjectLinkParams = {
  protocol: string;
  host: string;
  shortName: string;
  workflow?: string;
  commentId?: string;
  pageId?: string;
  itemId?: string;
  locale?: string;
};

export const generateWorkspaceMemberProjectLink = function (
  opts: generateWorkspaceMemberProjectLinkParams
) {
  const {shortName} = opts;

  const {cookedProtocol, cookedHost, queryStr} = getSharedLinkParams(opts);

  return `${cookedProtocol}//${cookedHost}/design/${shortName}?${queryStr}`;
};

const getSharedLinkParams = (opts: any) => {
  const {
    protocol,
    host,
    shortName,
    projectLinkId,
    workflow,
    commentId,
    pageId,
    itemId,
    locale,
  } = opts;

  const query: Record<string, string> = {
    utm_medium: 'project_link',
    utm_source: 'designer',
    utm_content: shortName,
  };

  // Add project parameter first if projectLinkId is defined
  if (projectLinkId) {
    query.project = projectLinkId;
  }

  if (workflow) {
    query.workflow = workflow;
  }

  if (commentId) {
    query.commentId = commentId;
  }

  if (pageId) {
    query.pageId = pageId;
  }

  if (itemId) {
    query.itemId = itemId;
  }

  if (locale) {
    query.locale = locale;
  }

  const queryStr = new URLSearchParams(query).toString();

  const isCurrentlyInPreview = host.startsWith('preview.');
  const isCurrentlyInAnonComments = host.startsWith('comments.');

  // When on a preview or comments domain, drop the `preview.` or `comments.` label so that we remain with the correct host, i.e `webflow.com`.
  // Otherwise, we're using designer subdomain (our URL is slug.design.bar.baz), so remove the first two labels (slug.design)
  const cookedHost =
    isCurrentlyInPreview || isCurrentlyInAnonComments
      ? host.replace(/^preview\./, '').replace(/^comments\./, '')
      : host.split('.').slice(2).join('.');

  // ensure protocol ends with a `:`
  const cookedProtocol = protocol.endsWith(':') ? protocol : `${protocol}:`;

  return {
    cookedProtocol,
    cookedHost,
    queryStr,
  };
};
